import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronDown, Bot, X, Send } from "lucide-react";
import { getAIBotReply } from "../../data/chatbot";

function ChatBot({ lang = "en" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [isMobileChatView, setIsMobileChatView] = useState(() => window.innerWidth < 480);
  useEffect(() => {
    const onResize = () => setIsMobileChatView(window.innerWidth < 480);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const shakeTimers = useRef([]);
  useEffect(() => {
    return () => shakeTimers.current.forEach(clearTimeout);
  }, []);
  const startSoundAndShake = () => {
    setShowBanner(false);
    shakeTimers.current.forEach(clearTimeout);
    shakeTimers.current = [];

    const playDing = (delayMs) => {
      const t = setTimeout(() => {
        try {
          const AudioCtx = window.AudioContext || window.webkitAudioContext;
          const ctx = new AudioCtx();
          const ct = ctx.currentTime;
          [[1318, ct, 0.15], [1568, ct + 0.18, 0.25]].forEach(([freq, start, dur]) => {
            const osc  = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = freq;
            osc.type = "sine";
            gain.gain.setValueAtTime(0, start);
            gain.gain.linearRampToValueAtTime(0.9, start + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
            osc.start(start);
            osc.stop(start + dur + 0.05);
          });
        } catch (e) {}
      }, delayMs);
      shakeTimers.current.push(t);
    };

    [0, 400, 800].forEach((delay) => {
      shakeTimers.current.push(setTimeout(() => setShake(true),  delay));
      shakeTimers.current.push(setTimeout(() => setShake(false), delay + 220));
      playDing(delay);
    });
  };

  const handleToggle = () => setIsOpen(v => !v);
  const INIT_MSG = {
    en: "Hello! 👋 Welcome to CodeKloud Academy!\n\nI know everything about our site — courses, mentors, certifications, free trainings and more. Ask me anything! 🎓",
    az: "Salam! 👋 CodeKloud Academy-ə xoş gəldiniz!\n\nMən saytdakı bütün məlumatları bilirəm — kurslar, mentorlar, sertifikatlar, pulsuz təlimlər və daha çox. Hər hansı sualınızı soruşun! 🎓",
    ru: "Привет! 👋 Добро пожаловать в CodeKloud Academy!\n\nЯ знаю всё о нашем сайте — курсы, менторы, сертификаты, бесплатные тренинги и многое другое. Спрашивайте! 🎓",
  };
  const [messages, setMessages] = useState([
    { role: "assistant", content: INIT_MSG[lang] || INIT_MSG.en },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(t);
  }, [isOpen]);

  const sendTimerRef = useRef(null);
  const sendMessage = useCallback((key, display) => {
    const msg = (key || input).trim();
    if (!msg || loading) return;
    const shown = display || msg;

    setMessages(prev => [...prev, { role: "user", content: shown }]);
    setInput("");
    setLoading(true);

    if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
    sendTimerRef.current = setTimeout(() => {
      const reply = getAIBotReply(msg, lang);
      setMessages(prev => [...prev, { role: "assistant", content: reply }]);
      setLoading(false);
    }, 500);
  }, [input, loading, lang]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(null); }
  };

  // label = göstərilən mətn, key = bota göndərilən sabit söz (dildən asılı olmayaraq eyni)
  const QUICK_QUESTIONS = {
    en: [
      { label: "Available courses", key: "__courses__" },
      { label: "Free trainings", key: "__free__" },
      { label: "Certificates", key: "__cert__" },
      { label: "Our mentors", key: "__mentors__" },
      { label: "Trial lesson", key: "__trial__" },
      { label: "Contact us", key: "__contact__" },
    ],
    az: [
      { label: "Hansı kurslar var?", key: "__courses__" },
      { label: "Pulsuz təlimlər", key: "__free__" },
      { label: "Sertifikatlar", key: "__cert__" },
      { label: "Mentorlar", key: "__mentors__" },
      { label: "Sınaq dərsi", key: "__trial__" },
      { label: "Müraciət et", key: "__contact__" },
    ],
    ru: [
      { label: "Доступные курсы", key: "__courses__" },
      { label: "Бесплатные тренинги", key: "__free__" },
      { label: "Сертификаты", key: "__cert__" },
      { label: "Наши менторы", key: "__mentors__" },
      { label: "Пробный урок", key: "__trial__" },
      { label: "Связаться", key: "__contact__" },
    ],
  };
  const quickQuestions = QUICK_QUESTIONS[lang] || QUICK_QUESTIONS.en;

  return (
    <>
      {/* Sound banner */}
      {showBanner && (
        <div className="fixed bottom-24 left-3 z-50 flex items-center gap-2 bg-white rounded-2xl shadow-2xl px-4 py-3 border border-gray-100 animate-bounce" style={{ maxWidth: 280 }}>
          <span className="text-2xl">🔔</span>
          <div className="flex flex-col">
            <span className="text-xs font-semibold text-gray-800">
              {lang === "en" ? "New notification!" : lang === "ru" ? "Новое уведомление!" : "Bildiriş var!"}
            </span>
            <span className="text-xs text-gray-500">
              {lang === "en" ? "CodeKloud Bot sent you a message" : lang === "ru" ? "CodeKloud Bot написал вам" : "CodeKloud Bot sizə yazdı"}
            </span>
          </div>
          <button onClick={startSoundAndShake} className="ml-1 text-xs bg-blue-900 text-white rounded-lg px-2 py-1 font-bold hover:bg-blue-700 transition-colors">
            {lang === "en" ? "Open" : lang === "ru" ? "Открыть" : "Aç"}
          </button>
          <button onClick={() => setShowBanner(false)} className="text-gray-300 hover:text-gray-500 text-base leading-none ml-1">✕</button>
        </div>
      )}

      {/* Float button */}
      <div className="fixed bottom-5 left-5 z-40">
        <button
          onClick={handleToggle}
          className="group relative flex items-center justify-center w-16 h-16 rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
          style={{ background: "linear-gradient(135deg, #1e3a8a, #F08232)", transform: shake ? "rotate(-22deg) scale(1.25)" : "rotate(0deg) scale(1)", transition: shake ? "transform 0.08s ease" : "transform 0.15s ease" }}
          aria-label="Open chat"
        >
          {isOpen ? <ChevronDown className="w-7 h-7 text-white" /> : <Bot className="w-7 h-7 text-white" />}
          {!isOpen && <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse" />}
          <span className="absolute left-full ml-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            CodeKloud Bot
            <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
          </span>
        </button>
      </div>

      {/* Chat window — mobil-friendly */}
      {isOpen && (
        <div
          className="fixed z-40 flex flex-col overflow-hidden"
          style={isMobileChatView ? {
            position: "fixed",
            bottom: 80, left: 0, right: 0,
            height: "75dvh",
            borderRadius: "20px 20px 0 0",
            boxShadow: "0 -4px 30px rgba(0,0,0,0.15)",
            background: "#fff",
            animation: "chatSlideUp 0.3s cubic-bezier(0.22,1,0.36,1)",
          } : {
            position: "fixed",
            bottom: 90, left: 16,
            width: 360, height: 580,
            borderRadius: 20,
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            border: "1px solid #e5e7eb",
            background: "#fff",
            animation: "chatSlideUp 0.3s cubic-bezier(0.22,1,0.36,1)",
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ background: "linear-gradient(135deg, #1e3a8a, #2d4fa3)" }}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-white font-bold text-sm">CodeKloud Bot</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-400 rounded-full" />
                  <span className="text-white/70 text-xs">Online</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/70 hover:text-white transition-colors w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" style={{ background: "#f8fafc", WebkitOverflowScrolling: "touch" }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                {msg.role === "assistant" && (
                  <div className="w-7 h-7 rounded-full bg-blue-900 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                )}
                <div
                  className="max-w-[82%] px-3 py-2.5 text-sm leading-relaxed"
                  style={{
                    background: msg.role === "user" ? "#1e3a8a" : "#fff",
                    color: msg.role === "user" ? "#fff" : "#1f2937",
                    borderRadius: msg.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-blue-900 flex items-center justify-center mr-2 flex-shrink-0">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="px-4 py-3 bg-white rounded-2xl shadow-sm flex items-center gap-1">
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="w-2 h-2 bg-gray-400 rounded-full" style={{ animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Quick questions — hər bot cavabından sonra */}
          {messages[messages.length - 1]?.role === "assistant" && !loading && (
            <div className="px-3 py-2 flex-shrink-0 flex flex-wrap gap-2 border-t border-gray-100" style={{ background: "#f8fafc", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {quickQuestions.map(({ label, key }) => (
                <button
                  key={key}
                  onClick={() => sendMessage(key, label)}
                  className="flex-shrink-0 text-xs px-3 py-2 rounded-full border border-blue-200 text-blue-800 bg-white active:bg-blue-50 transition-colors"
                  style={{ touchAction: "manipulation", minHeight: 36 }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="flex items-end gap-2 px-3 py-3 border-t border-gray-100 flex-shrink-0 bg-white" style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder={lang === "en" ? "Ask your question..." : lang === "ru" ? "Задайте вопрос..." : "Sualınızı yazın..."}
              rows={1}
              className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-blue-400 transition-colors"
              style={{ maxHeight: 100, lineHeight: 1.5, fontSize: 16 }}
            />
            <button
              onClick={() => sendMessage(null)}
              disabled={!input.trim() || loading}
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-all"
              style={{ background: input.trim() && !loading ? "#1e3a8a" : "#e5e7eb", color: input.trim() && !loading ? "#fff" : "#9ca3af", touchAction: "manipulation" }}
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </>
  );
}

export { ChatBot };
